import { InjectionToken } from '@angular/core';

import { from, lastValueFrom, Observable } from 'rxjs';

import { _getDefaultCacheEntryOptions, BarCacheManager, BarCacheStore, BarCacheStoreOptions, getParamNames } from '@lvflanders/bar';

import { baasCoreContext } from '../baas-core.context';
import { BarCacheableOptions } from '../models/bar-cacheable-options.model';

function defineCacheKey(cacheKeyPrefix: string, paramNames: any[], args: any[]): string {
    // no arguments just use cache key prefix (class + method)
    if (paramNames.length == 0) {
        return cacheKeyPrefix;
    }
    if (paramNames.length != args.length) {
        // we cannot match the parameters names with the arguments so we just stringify entire args
        return `${cacheKeyPrefix}_${JSON.stringify(args)}`;
    }
    // we have matching parameters, loop through them and generate a full cache key
    let result = cacheKeyPrefix;
    for (let i = 0; i < paramNames.length; i++) {
        result += `_${paramNames[i]}:${JSON.stringify(args[i])}`;
    }
    return result;
}
/**
 * Decorator to use to use on functions which return an observable which will use @lvflanders/bar caching.
 * Note that because it uses async cache it cannot be used on methods which do not return Observable.
 * @param options
 */
export function BarCacheable(options?: BarCacheableOptions): MethodDecorator {
    return function (target: object, key: string, descriptor: any) {
        // original method
        const originalMethod = descriptor.value;
        const className = target.constructor != null ? target.constructor.name : null;
        const cacheKeyPrefix = options == null || options.key == null ? `${className}_${key}` : options.key;
        const paramNames = getParamNames(descriptor.value);
        descriptor.value = function (...args: any[]) {
            const cacheKey = defineCacheKey(cacheKeyPrefix, paramNames, args);
            let cacheStore: BarCacheStore;

            if (options != null && options.store != null) {
                if (options.store instanceof InjectionToken) {
                    // we have an injection token use this
                    cacheStore = baasCoreContext.injector.get(options.store as InjectionToken<BarCacheStore>);
                } else {
                    const manager = baasCoreContext.injector.get(BarCacheManager);
                    cacheStore = manager.getStore(options.store as BarCacheStoreOptions);
                }
            } else {
                // get the default one which is for the current app
                cacheStore = baasCoreContext.injector.get(BarCacheStore);
            }

            if (options != null && options.promise) {
                // consumer wants to use a promise return type
                return cacheStore.getOrSetItem(
                    cacheKey,
                    () => {
                        // callback in case not in cache yet
                        // get original result and we make assumption that an observable is used because this is what is mostly used in Angular applications
                        const originalResult = originalMethod.apply(this, args);

                        if (originalResult instanceof Promise) {
                            // convert it back to a promise because @lvflanders/bar uses promises to be more loosely coupled
                            return originalResult as Promise<any>;
                        } else {
                            Promise.reject(
                                'Functions using BarCacheable from @lvflanders/bar-ng decorator must return a Promise in case BarCacheableOptions.promise equals true.'
                            );
                        }
                    },
                    options != null ? options.options : _getDefaultCacheEntryOptions()
                );
            } else {
                // consumer wants to use default Observable return type
                // get the item through the cache and converts it to observable because we cannot detect at runtime what the return type of a method is without executing it
                return from(
                    cacheStore.getOrSetItem(
                        cacheKey,
                        () => {
                            // callback in case not in cache yet
                            // get original result and we make assumption that an observable is used because this is what is mostly used in Angular applications
                            const originalResult = originalMethod.apply(this, args);

                            if (originalResult instanceof Observable) {
                                // convert it back to a promise because @lvflanders/bar uses promises to be more loosely coupled
                                return lastValueFrom(originalResult as Observable<any>);
                            } else {
                                Promise.reject(
                                    'Functions using BarCacheable from @lvflanders/bar-ng decorator must return an Observable in case BarCacheableOptions.promise is not equal true.'
                                );
                            }
                        },
                        options != null ? options.options : _getDefaultCacheEntryOptions()
                    )
                );
            }
        };

        return descriptor;
    };
}
